import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Upload, RefreshCw, Check, ImageOff } from "lucide-react";
import { toast } from "sonner";

export default function AdminMissingImages() {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [uploadingId, setUploadingId] = useState(null);

  const scan = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/products/check-images");
      setProducts(r.data.products);
    } catch (e) {
      toast.error("Scan failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { scan(); }, []);

  const applyUrl = async (product, url) => {
    if (!url.trim()) return;
    setUploadingId(product.id);
    try {
      const r = await api.post("/admin/upload-from-url", { url: url.trim() });
      await api.patch(`/admin/products/${product.id}/images`, { images: [r.data.url] });
      setProducts((prev) => prev.filter((p) => p.id !== product.id));
      toast.success(`Image set for "${product.name}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setUploadingId(null);
    }
  };

  const onUpload = async (product, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingId(product.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = r.data.url;
      await applyUrl(product, url);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploadingId(null);
      e.target.value = "";
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black">Missing Images</h1>
          <p className="text-sm text-slate-400 mt-1">
            Products whose linked images are broken or empty. Paste a URL or upload a file to fix each one.
          </p>
        </div>
        <button
          onClick={scan}
          disabled={loading}
          className="flex items-center gap-2 bg-navy-700 border border-gold/20 rounded-md px-4 py-2 text-xs uppercase tracking-wider hover:border-gold/50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Rescan
        </button>
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm">Scanning all product image URLs…</div>
      ) : products.length === 0 ? (
        <div className="flex items-center gap-2 text-emerald-400 text-sm">
          <Check className="w-4 h-4" /> No missing images — every product has at least one valid image.
        </div>
      ) : (
        <div className="grid gap-3">
          {products.map((p) => (
            <div key={p.id} className="bg-navy-800 border border-gold/20 rounded-md p-4 flex items-center gap-4">
              <div className="w-16 h-16 rounded bg-navy-700 border border-gold/10 flex items-center justify-center shrink-0">
                <ImageOff className="w-6 h-6 text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{p.name}</div>
                {p.broken_images.length > 0 && (
                  <div className="text-[10px] text-rose-400 truncate mt-0.5">
                    {p.broken_images.length} broken URL{p.broken_images.length > 1 ? "s" : ""}: {p.broken_images[0]}
                  </div>
                )}
              </div>
              <div className="flex gap-2 items-center shrink-0">
                <label className="cursor-pointer flex items-center gap-2 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-xs hover:border-gold/50">
                  <Upload className="w-4 h-4 text-gold" /> {uploadingId === p.id ? "Uploading..." : "Upload"}
                  <input type="file" accept="image/*" onChange={(e) => onUpload(p, e)} className="hidden" />
                </label>
                <input
                  placeholder="or paste image URL"
                  disabled={uploadingId === p.id}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") applyUrl(p, e.currentTarget.value);
                  }}
                  className="w-64 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-xs focus:outline-none focus:border-gold/60 disabled:opacity-50"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
